import React, { useState, useContext, useEffect } from 'react';
import { Container, Form, Button, Alert, Row, Col, Card } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import PhoneNumberInput from '../components/PhoneNumberInput';
import { normalizePhoneNumber } from '../utils/phoneUtils';

const BACKEND_URL = process.env.REACT_APP_RXROOSTER_BACKEND_URL || "http://localhost:3001";

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useContext(AuthContext);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'CRNA', // options: 'CRNA' or 'GROUP'
  });
  const [smsSent, setSmsSent] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [smsError, setSmsError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  
  // Form validation state
  const [formErrors, setFormErrors] = useState({});
  const [phoneValid, setPhoneValid] = useState(false);
  const [codeValid, setCodeValid] = useState(false);

  // Check if we have a pre-filled phone number from login page
  useEffect(() => {
    if (location.state?.prefillPhone) {
      const normalizedPhone = normalizePhoneNumber(location.state.prefillPhone);
      setFormData(prev => ({ ...prev, phone: normalizedPhone }));
      // Auto-focus on the name field since we already have their phone
      const nameInput = document.getElementById('registerName');
      if (nameInput) nameInput.focus();
    }
  }, [location.state]);

  // Validate verification code when it changes
  useEffect(() => {
    // Simple validation - code should be 6 digits
    setCodeValid(smsCode.length >= 4 && /^\d+$/.test(smsCode));
  }, [smsCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear any error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Handle phone change separately
  const handlePhoneChange = (phone) => {
    setFormData(prev => ({ ...prev, phone }));
    if (formErrors.phone) {
      setFormErrors(prev => ({ ...prev, phone: null }));
    }
  };

  // Function to send the SMS verification code
  const sendVerificationSMS = async () => {
    setSmsError('');
    
    // Validate required fields first
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Full name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!phoneValid) errors.phone = 'Please enter a valid phone number';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/auth/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formData.phone }),
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to send verification code. Please try again.');
      }
      
      setSmsSent(true);
    } catch (error) {
      setSmsError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Function to verify the SMS code and finalize registration
  const verifyAndRegister = async () => {
    if (!codeValid) {
      setSmsError('Please enter a valid verification code (at least 4 digits)');
      return;
    }
    
    setSubmitting(true);
    setSmsError('');
    
    try {
      // First verify the SMS code
      const verifyRes = await fetch(`${BACKEND_URL}/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          phone: formData.phone, 
          code: smsCode 
        }),
      });
      
      if (!verifyRes.ok) {
        const errorData = await verifyRes.json().catch(() => ({}));
        throw new Error(errorData.message || 'Invalid verification code. Please try again.');
      }
      
      // If verification is successful, register the user
      const registerRes = await fetch(`${BACKEND_URL}/provider/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          role: formData.role
        }),
      });
      
      const registerData = await registerRes.json();
      
      if (!registerRes.ok) {
        throw new Error(registerData.message || 'Registration failed. Please try again.');
      }
      
      // Registration successful!
      setIsSubmitted(true);
      setStatusMsg('Registration successful! Logging you in...');
      
      // Automatically log in the user
      if (registerData.token) {
        login(registerData.token);
        
        // Navigate based on role
        setTimeout(() => {
          if (formData.role === 'GROUP') {
            navigate('/group-intake');
          } else {
            navigate('/profile'); // Default for CRNA
          }
        }, 1500);
      } else {
        setStatusMsg('Registration successful! Please log in.');
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (error) {
      setSmsError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow-sm p-4">
            <h1 className="text-center mb-4">Join the Movement</h1>
            
            {isSubmitted ? (
              <Alert variant="success" className="text-center">
                {statusMsg}
              </Alert>
            ) : smsSent ? (
              <>
                {smsError && <Alert variant="danger">{smsError}</Alert>}
                
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number Verification</Form.Label>
                  <p className="text-muted">
                    We've sent a verification code to <strong>{formData.phone}</strong>
                  </p>
                  <Form.Control
                    type="text"
                    value={smsCode}
                    onChange={(e) => setSmsCode(e.target.value)}
                    placeholder="Enter verification code"
                    className={smsCode ? (codeValid ? 'is-valid' : 'is-invalid') : ''}
                    maxLength={6}
                    inputMode="numeric"
                    autoFocus
                  />
                </Form.Group>
                
                <div className="d-flex justify-content-between">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSmsSent(false)}
                    disabled={submitting}
                  >
                    Back
                  </Button>
                  <Button
                    variant="success"
                    onClick={verifyAndRegister}
                    disabled={submitting || !codeValid}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Verifying...
                      </>
                    ) : (
                      'Register'
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <Form>
                {smsError && <Alert variant="danger">{smsError}</Alert>}
                
                <Form.Group className="mb-3">
                  <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    id="registerName"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    isInvalid={!!formErrors.name}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.name}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    isInvalid={!!formErrors.email}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.email}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <PhoneNumberInput
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  required={true}
                  label="Phone Number"
                  id="registerPhone"
                  onValidityChange={setPhoneValid}
                />
                {formErrors.phone && (
                  <Alert variant="danger" className="py-1 mt-n2 mb-3">
                    {formErrors.phone}
                  </Alert>
                )}
                
                <Form.Group className="mb-4">
                  <Form.Label>I am a: <span className="text-danger">*</span></Form.Label>
                  <div>
                    <Form.Check
                      inline
                      type="radio"
                      label="CRNA / Provider"
                      name="role"
                      id="role-crna"
                      value="CRNA"
                      checked={formData.role === 'CRNA'}
                      onChange={handleChange}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      label="Group / Facility"
                      name="role"
                      id="role-group"
                      value="GROUP"
                      checked={formData.role === 'GROUP'}
                      onChange={handleChange}
                    />
                  </div>
                </Form.Group>
                
                <Button
                  variant="primary"
                  onClick={sendVerificationSMS}
                  disabled={submitting || !phoneValid || !formData.name || !formData.email}
                  className="mb-2 w-100"
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Sending Code...
                    </>
                  ) : (
                    'Send Verification Code'
                  )}
                </Button>
              </Form>
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Register;
